/**
 * Reports Page - Página de Relatórios com exportação PDF
 */
import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { employeesAPI, performanceCyclesAPI, candidatesAPI, jobsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  FileText,
  Download,
  Loader2,
  Users,
  Briefcase,
  BarChart3,
  TrendingUp,
  Calendar,
  Building2,
  UserCheck,
  Clock,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function ReportsPage() {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedReport, setSelectedReport] = useState('overview');
  const [stats, setStats] = useState({
    employees: { total: 0, active: 0, inactive: 0 },
    jobs: { total: 0, active: 0, applications: 0 },
    cycles: { total: 0, active: 0 },
    candidates: { searched: 0, selected: 0 }
  });
  
  const reportRef = useRef(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [employeesRes, jobsRes, cyclesRes] = await Promise.all([
        employeesAPI.getAll({ limit: 1000 }),
        jobsAPI.getMy(),
        performanceCyclesAPI.getAll({ limit: 100 })
      ]);

      const employees = employeesRes.data.employees || [];
      const jobs = jobsRes.data || [];
      const cycles = cyclesRes.data.cycles || [];

      setStats({
        employees: {
          total: employees.length,
          active: employees.filter(e => e.situacao === 'ativo').length,
          inactive: employees.filter(e => e.situacao !== 'ativo').length
        },
        jobs: {
          total: jobs.length,
          active: jobs.filter(j => j.status === 'active').length,
          applications: jobs.reduce((sum, j) => sum + (j.applications_count || 0), 0)
        },
        cycles: {
          total: cycles.length,
          active: cycles.filter(c => c.status === 'em_andamento').length
        },
        candidates: {
          searched: jobs.reduce((sum, j) => sum + (j.views_count || 0), 0),
          selected: jobs.reduce((sum, j) => sum + (j.selected_count || 0), 0)
        }
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async () => {
    if (!reportRef.current) return;
    
    setGenerating(true);
    try {
      const element = reportRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const date = new Date().toISOString().split('T')[0];
      pdf.save(`relatorio-g2gente-${selectedReport}-${date}.pdf`);
      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Erro ao gerar PDF');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-cyan-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Relatórios</h1>
              <p className="text-slate-600">Visualize e exporte relatórios da plataforma</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={selectedReport} onValueChange={setSelectedReport}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Tipo de relatório" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="overview">Visão Geral</SelectItem>
                <SelectItem value="recruitment">Recrutamento</SelectItem>
                <SelectItem value="employees">Funcionários</SelectItem>
                <SelectItem value="performance">Desempenho</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              onClick={generatePDF}
              disabled={generating}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="export-pdf-btn"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Exportar PDF
            </Button>
          </div>
        </div>

        {/* Report Content */}
        <div ref={reportRef} className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          {/* Report Header */}
          <div className="flex items-center justify-between border-b border-slate-200 pb-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">G2</span>
              </div>
              <div>
                <h2 className="font-bold text-slate-900">G2eGente</h2>
                <p className="text-sm text-slate-500">Relatório Gerencial</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-600">Data de Geração</p>
              <p className="font-medium text-slate-900">
                {new Date().toLocaleDateString('pt-BR', { 
                  day: '2-digit', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </p>
            </div>
          </div>

          {/* Overview Report */}
          {selectedReport === 'overview' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-cyan-600" />
                Visão Geral da Plataforma
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                  icon={Users}
                  label="Funcionários"
                  value={stats.employees.total}
                  subtext={`${stats.employees.active} ativos`}
                  color="cyan"
                />
                <StatCard
                  icon={Briefcase}
                  label="Vagas"
                  value={stats.jobs.total}
                  subtext={`${stats.jobs.active} ativas`}
                  color="blue"
                />
                <StatCard
                  icon={UserCheck}
                  label="Candidaturas"
                  value={stats.jobs.applications}
                  subtext="total recebidas"
                  color="green"
                />
                <StatCard
                  icon={TrendingUp}
                  label="Ciclos Avaliação"
                  value={stats.cycles.total}
                  subtext={`${stats.cycles.active} em andamento`}
                  color="purple"
                />
              </div>
            </div>
          )}

          {/* Recruitment Report */}
          {selectedReport === 'recruitment' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-cyan-600" />
                Relatório de Recrutamento
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard
                  icon={Briefcase}
                  label="Total de Vagas"
                  value={stats.jobs.total}
                  subtext="publicadas"
                  color="cyan"
                />
                <StatCard
                  icon={CheckCircle2}
                  label="Vagas Ativas"
                  value={stats.jobs.active}
                  subtext="recebendo candidaturas"
                  color="green"
                />
                <StatCard
                  icon={UserCheck}
                  label="Candidaturas"
                  value={stats.jobs.applications}
                  subtext="recebidas no total"
                  color="blue"
                />
              </div>

              <Separator />

              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-900 mb-3">Métricas de Conversão</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Taxa de Candidaturas por Vaga</span>
                    <Badge variant="secondary">
                      {stats.jobs.total > 0 
                        ? (stats.jobs.applications / stats.jobs.total).toFixed(1) 
                        : 0} média
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Vagas com Candidaturas</span>
                    <Badge variant="secondary">
                      {stats.jobs.active} de {stats.jobs.total}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Employees Report */}
          {selectedReport === 'employees' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-600" />
                Relatório de Funcionários
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard
                  icon={Users}
                  label="Total de Funcionários"
                  value={stats.employees.total}
                  subtext="cadastrados"
                  color="cyan"
                />
                <StatCard
                  icon={CheckCircle2}
                  label="Ativos"
                  value={stats.employees.active}
                  subtext="em atividade"
                  color="green"
                />
                <StatCard
                  icon={AlertCircle}
                  label="Inativos"
                  value={stats.employees.inactive}
                  subtext="desligados/afastados"
                  color="red"
                />
              </div>

              <Separator />

              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-900 mb-3">Distribuição</h4>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="h-4 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 rounded-full transition-all"
                        style={{ 
                          width: stats.employees.total > 0 
                            ? `${(stats.employees.active / stats.employees.total) * 100}%` 
                            : '0%' 
                        }}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-medium text-slate-700">
                    {stats.employees.total > 0 
                      ? Math.round((stats.employees.active / stats.employees.total) * 100) 
                      : 0}% ativos
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Performance Report */}
          {selectedReport === 'performance' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-cyan-600" />
                Relatório de Desempenho
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <StatCard
                  icon={Calendar}
                  label="Ciclos de Avaliação"
                  value={stats.cycles.total}
                  subtext="criados"
                  color="cyan"
                />
                <StatCard
                  icon={Clock}
                  label="Em Andamento"
                  value={stats.cycles.active}
                  subtext="ciclos ativos"
                  color="amber"
                />
              </div>

              <Separator />

              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-900 mb-3">Status dos Ciclos</h4>
                <p className="text-slate-600 text-sm">
                  {stats.cycles.active > 0 
                    ? `Você tem ${stats.cycles.active} ciclo(s) de avaliação em andamento.`
                    : 'Nenhum ciclo de avaliação em andamento. Crie um novo ciclo para iniciar as avaliações.'}
                </p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 pt-4 border-t border-slate-200">
            <p className="text-xs text-slate-400 text-center">
              Relatório gerado automaticamente pela plataforma G2eGente • 
              {new Date().toLocaleString('pt-BR')}
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, label, value, subtext, color }) {
  const colors = {
    cyan: 'bg-cyan-50 text-cyan-600',
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600'
  };

  return (
    <Card className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
            <p className="text-sm font-medium text-slate-700">{label}</p>
            <p className="text-xs text-slate-500">{subtext}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
